/**
 * legalMarkdown.ts — Rechtstexte aus `legal_documents` darstellen.
 *
 * Wortgleiche Portierung von `markdownToHtml()` aus
 * studio/server/services/consentService.js. Zwei Kopien, weil Server und
 * Frontend getrennte Auslieferungseinheiten sind — dieselbe Konvention wie bei
 * `contentLang.ts` / `aiContent.ts`: identisch halten, `diff` hält sie ehrlich.
 *
 * Bewusst KEINE Markdown-Bibliothek: der Renderer sieht ausschliesslich die
 * wenigen Konstrukte, die in den Rechtstexten vorkommen, und alles wird vorher
 * escaped. Es entsteht nur das Markup, das hier erzeugt wird — deshalb ist die
 * Ausgabe für `v-html` unbedenklich, auch ohne Sanitizer.
 */

export function escapeHtml(s: unknown): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string)
  );
}

/** Fett, kursiv, Code — angewandt NACH dem Escapen, nie davor. */
function inlineMd(text: string): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])\*([^*\n]+)\*/g, "$1<em>$2</em>");
}

export function legalMarkdownToHtml(md: string): string {
  const lines = String(md || "").replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let para: string[] = [];
  let list: string | null = null;
  let quote: string[] = [];
  let table: { head: string[]; rows: string[][] } | null = null;

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inlineMd(para.join(" "))}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      out.push("</ul>");
      list = null;
    }
  };
  const flushQuote = () => {
    if (quote.length) {
      out.push(`<blockquote>${inlineMd(quote.join(" "))}</blockquote>`);
      quote = [];
    }
  };
  const flushTable = () => {
    if (!table) return;
    const head = table.head.map((c) => `<th>${inlineMd(c)}</th>`).join("");
    const body = table.rows
      .map((r) => `<tr>${r.map((c) => `<td>${inlineMd(c)}</td>`).join("")}</tr>`)
      .join("");
    out.push(
      `<div class="tablewrap"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`
    );
    table = null;
  };
  const flushAll = () => {
    flushPara();
    flushList();
    flushQuote();
    flushTable();
  };

  const cells = (line: string) =>
    line.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (!line.trim()) {
      flushAll();
      continue;
    }

    // Tabelle: die Kopfzeile erkennt man erst an der Trennzeile darunter,
    // deshalb wird eine offene Tabelle zeilenweise weitergefüllt.
    if (/^\|.*\|$/.test(line.trim())) {
      if (/^\|[\s:|-]+\|$/.test(line.trim())) continue; // Trennzeile
      flushPara();
      flushList();
      flushQuote();
      if (!table) table = { head: cells(line.trim()), rows: [] };
      else table.rows.push(cells(line.trim()));
      continue;
    }
    flushTable();

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      flushAll();
      const level = Math.min(heading[1].length + 1, 5); // # → h2, die Seite behält EIN h1
      out.push(`<h${level}>${inlineMd(heading[2])}</h${level}>`);
      continue;
    }

    if (/^>\s?/.test(line)) {
      flushPara();
      flushList();
      quote.push(line.replace(/^>\s?/, ""));
      continue;
    }
    flushQuote();

    const item = /^[-*]\s+(.*)$/.exec(line);
    if (item) {
      flushPara();
      if (!list) {
        out.push("<ul>");
        list = "ul";
      }
      out.push(`<li>${inlineMd(item[1])}</li>`);
      continue;
    }
    flushList();

    para.push(line.trim());
  }
  flushAll();
  return out.join("\n");
}
